import { CheckCircle, RadioButtonUnchecked } from "@mui/icons-material"
import { Box, Divider, Tooltip } from "@mui/material"
import { intl } from "src/internationalization/intl"
import { formatCurrencyBRL } from "src/lib/transformers/ClientsOverviewInfo"
import { Typography } from "../../basics"

export type MonitoringMetricMode = "volume" | "clients"

export interface AdvisorChartPortfolio {
    label: string
    account: string
    opportunityVolume: number | null
    executedValue: number
    percentRatio: number | null
}

export interface AdvisorChartRow {
    groupKey: string
    advisorLabel: string
    opportunitySum: number
    liquidoSum: number
    percentRatio: number | null
    /** Clientes com % de volume financeiro > 0. */
    clientsDone: number
    /** Total de clientes (carteiras) do assessor. */
    clientsTotal: number
    portfolios: AdvisorChartPortfolio[]
}

/** Cliente "feito" = qualquer execução de volume financeiro acima de 0%. */
export const isClientExecuted = (percentRatio: number | null | undefined): boolean =>
    percentRatio != null && Number.isFinite(percentRatio) && percentRatio > 0

export const countClientsDone = (
    portfolios: Array<{ percentRatio: number | null; account?: string }>,
    uniqueByAccount = false,
): { clientsDone: number; clientsTotal: number } => {
    if (!uniqueByAccount) {
        const clientsDone = portfolios.filter((p) => isClientExecuted(p.percentRatio)).length
        return { clientsDone, clientsTotal: portfolios.length }
    }
    const byAccount = new Map<string, boolean>()
    for (const p of portfolios) {
        const key = (p.account ?? "").trim() || `anon:${byAccount.size}`
        const done = isClientExecuted(p.percentRatio)
        byAccount.set(key, (byAccount.get(key) ?? false) || done)
    }
    let clientsDone = 0
    for (const done of Array.from(byAccount.values())) {
        if (done) clientsDone += 1
    }
    return { clientsDone, clientsTotal: byAccount.size }
}

export const formatClientsDone = (done: number, total: number): string =>
    intl("objectiveMonitoringClientsDoneShort", {
        values: { done: String(done), total: String(total) },
    })

export const clientsCoverageRatio = (done: number, total: number): number | null =>
    total > 0 ? done / total : null

export const displayRatioForAdvisor = (
    row: Pick<AdvisorChartRow, "percentRatio" | "clientsDone" | "clientsTotal">,
    metric: MonitoringMetricMode,
): number | null =>
    metric === "clients"
        ? clientsCoverageRatio(row.clientsDone, row.clientsTotal)
        : row.percentRatio

export const displayRatioForPortfolio = (
    p: Pick<AdvisorChartPortfolio, "percentRatio">,
    metric: MonitoringMetricMode,
): number | null => {
    if (metric === "volume") return p.percentRatio
    return isClientExecuted(p.percentRatio) ? 1 : 0
}

export const formatPercent = (ratio: number | null): string =>
    ratio === null
        ? intl("objectiveMonitoringNA")
        : new Intl.NumberFormat("pt-BR", {
              style: "percent",
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
          }).format(ratio)

/** Cor da barra conforme o percentual executado (semáforo: baixo→alto). */
export const barColorForRatio = (ratio: number | null): string => {
    if (ratio === null) return "neutral.dark"
    if (ratio < 0.34) return "accentError.main"
    if (ratio < 0.67) return "secondary.main"
    return "accentSuccess.main"
}

export const clientStatusColor = (done: boolean): string =>
    done ? "accentSuccess.main" : "accentError.main"

const PortfolioBreakdown = ({ row }: { row: AdvisorChartRow }) => (
    <Box sx={{ p: 0.5, minWidth: 240, maxWidth: 320 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
            {row.advisorLabel || intl("objectiveMonitoringNoAdvisor")}
        </Typography>
        <Typography variant="caption" color="quaternary" sx={{ display: "block" }}>
            {`${intl("objectiveMonitoringChartConsolidated")}: ${formatCurrencyBRL(row.liquidoSum)} / ${formatCurrencyBRL(
                row.opportunitySum,
            )} · ${formatPercent(row.percentRatio)}`}
        </Typography>
        <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mt: 0.25 }}>
            {intl("objectiveMonitoringClientsDoneLabel", {
                values: { done: String(row.clientsDone), total: String(row.clientsTotal) },
            })}
        </Typography>
        <Divider sx={{ my: 0.75, borderColor: "rgba(255, 255, 255, 0.12)" }} />
        <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
            {`${intl("objectiveMonitoringChartPortfolios")} (${row.portfolios.length})`}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {row.portfolios.map((p, idx) => {
                const done = isClientExecuted(p.percentRatio)
                return (
                    <Box key={`${p.account}-${idx}`}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography variant="caption" sx={{ display: "block", fontWeight: 600, flex: 1 }}>
                                {p.label}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, color: clientStatusColor(done), flexShrink: 0 }}
                            >
                                {done
                                    ? intl("objectiveMonitoringClientDone")
                                    : intl("objectiveMonitoringClientNotDone")}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
                            <Typography variant="caption" color="quaternary">
                                {`${intl("objectiveMonitoringAmountCol")}: ${formatCurrencyBRL(p.executedValue)}`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: barColorForRatio(p.percentRatio), fontWeight: 700 }}>
                                {formatPercent(p.percentRatio)}
                            </Typography>
                        </Box>
                    </Box>
                )
            })}
        </Box>
    </Box>
)

export interface AdvisorExecutionChartProps {
    rows: AdvisorChartRow[]
    selectedKeys?: string[]
    onToggle?: (groupKey: string) => void
    metricMode?: MonitoringMetricMode
}

const AdvisorExecutionChart = ({
    rows,
    selectedKeys,
    onToggle,
    metricMode = "volume",
}: AdvisorExecutionChartProps) => {
    if (!rows.length) {
        return (
            <Typography variant="caption" color="secondary">
                {intl("objectiveMonitoringChartEmpty")}
            </Typography>
        )
    }

    const selectable = typeof onToggle === "function"

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {rows.map((row) => {
                const ratio = displayRatioForAdvisor(row, metricMode)
                const fillPct = ratio === null ? 0 : Math.max(0, Math.min(1, ratio)) * 100
                const selected = !!selectedKeys?.includes(row.groupKey)
                const secondaryLabel =
                    metricMode === "volume"
                        ? `${formatCurrencyBRL(row.liquidoSum)} / ${formatCurrencyBRL(row.opportunitySum)}`
                        : formatClientsDone(row.clientsDone, row.clientsTotal)
                return (
                    <Tooltip key={row.groupKey} arrow placement="top" title={<PortfolioBreakdown row={row} />}>
                        <Box
                            onClick={selectable ? () => onToggle!(row.groupKey) : undefined}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                cursor: selectable ? "pointer" : "default",
                                px: 0.75,
                                py: 0.5,
                                borderRadius: 1,
                                borderLeft: "3px solid",
                                borderLeftColor: selected ? "primary.main" : "transparent",
                                bgcolor: selected ? "neutral.darker" : "transparent",
                                transition: "background-color 120ms ease",
                                "&:hover": { bgcolor: "neutral.darker" },
                            }}
                        >
                            {selectable &&
                                (selected ? (
                                    <CheckCircle sx={{ fontSize: 16, color: "primary.main", flexShrink: 0 }} />
                                ) : (
                                    <RadioButtonUnchecked sx={{ fontSize: 16, color: "neutral.dark", flexShrink: 0 }} />
                                ))}
                            <Box sx={{ width: 132, flexShrink: 0, overflow: "hidden" }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                    {row.advisorLabel || intl("objectiveMonitoringNoAdvisor")}
                                </Typography>
                                <Typography variant="caption" color="quaternary" noWrap>
                                    {secondaryLabel}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 60,
                                    height: 18,
                                    borderRadius: 1,
                                    bgcolor: "neutral.darkest",
                                    border: "1px solid rgba(255, 255, 255, 0.06)",
                                    overflow: "hidden",
                                }}
                                title={
                                    metricMode === "clients"
                                        ? intl("objectiveMonitoringClientsAdesaoHint")
                                        : intl("objectiveMonitoringVolumeHint")
                                }
                            >
                                <Box
                                    sx={{
                                        width: `${fillPct}%`,
                                        height: "100%",
                                        bgcolor: barColorForRatio(ratio),
                                        transition: "width 240ms ease",
                                    }}
                                />
                            </Box>

                            <Box sx={{ width: 56, flexShrink: 0, textAlign: "right" }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: barColorForRatio(ratio) }}>
                                    {formatPercent(ratio)}
                                </Typography>
                            </Box>
                        </Box>
                    </Tooltip>
                )
            })}
        </Box>
    )
}

export { AdvisorExecutionChart }
